import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookMarked, ArrowLeft, ArrowRight, User, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { CourseThumbnail } from "@/components/CourseThumbnail";
import { StarRating } from "@/components/StarRating";

export default function MyPurchasesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/courses/my-purchases").then((res) => setCourses(res.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="my-purchases-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="my-purchases-page">
      <Link to="/courses" data-testid="my-purchases-back-link" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Courses
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <BookMarked className="w-6 h-6 text-pink-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-pink-400">My Learning</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">My Purchases</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">Every course you own — free or paid — in one place. Pick up right where you left off.</p>

      {courses.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center mt-8" data-testid="my-purchases-empty">
          <ShoppingBag className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">You haven't taken any courses yet.</p>
          <Link to="/courses" className="inline-block mt-4 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-5 py-2.5 rounded-lg">
            Browse Courses
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {courses.map((c, i) => (
            <motion.div key={c.course_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <Link to={`/courses/${c.course_id}`} data-testid={`my-purchase-card-${c.course_id}`} className="glass-panel rounded-2xl p-5 flex flex-col h-full group hover:-translate-y-1 transition-transform">
                <CourseThumbnail url={c.thumbnail_url} className="mb-4" />
                <h2 className="font-heading text-base font-semibold text-white leading-snug group-hover:text-pink-300 transition-colors">{c.title}</h2>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1.5">
                  <User className="w-3 h-3" /> {c.creator_name || "Creator"}
                </div>
                {c.my_rating ? (
                  <div className="flex items-center gap-1.5 mt-2" data-testid={`my-purchase-my-rating-${c.course_id}`}>
                    <StarRating rating={c.my_rating} size="w-3 h-3" />
                    <span className="text-[11px] text-slate-500">Your rating</span>
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-2">Rate this course after learning</p>
                )}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs font-semibold text-amber-300">{c.is_free ? "Free" : `Paid ₹${c.amount_paid.toLocaleString("en-IN")}`}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-primary/80 group-hover:bg-primary px-3 py-1.5 rounded-lg transition-colors">
                    Continue <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
